import React, {useCallback, useState} from 'react';
import {Dialog, Heading, Pane, PlusIcon} from "evergreen-ui";
import {AutocompleteProviderInput} from "../component/ProviderUserForm/component/AutocompleteProviderInput";
import {FormProvider, useForm} from "react-hook-form";
import {CancelButton, OkButton} from "../component/ProviderUserForm/component/FormButtons";
import {useCurrentUser} from "../../security";
import {ReadonlyProviderInput} from "../component/ProviderUserForm/component/ReadonlyProviderInput";
import {useProviderUserImportMutation$} from "./hook/useProviderUserImportMutation$";
import {ProviderUserImportInput} from "../domain/types";
import {CSVImportUserInput} from "./CSVImportUserInput";
import {CloseButton} from "../../common";
import {USER_POPUP_HEIGHT} from "../component/const";
import { ApplicationRootUrl, useApplicationRoot } from 'common/component/ApplicationRootContext';

type ProviderUserImportPopupProps = {
    onSuccess?: () => Promise<void>;
}
export const ProviderUserImportPopup = ({onSuccess}: ProviderUserImportPopupProps) => {
    const [showPopup, setShowPopup] = useState(false);
    const closePopup = useCallback(() => {
        setShowPopup(false);
    }, []);

    const successCallback = useCallback(() => {
        setShowPopup(false);
        if (onSuccess) {
            onSuccess();
        }
    }, [onSuccess]);

    return (<>
        <button
            onClick={() => setShowPopup(true)}
            className='bg-white h-10 w-32 my-4 py-2 px-1 text-atlantis-200 border
                hover:bg-gray-200 border-atlantis-200 font-semibold disabled:opacity-50
                text-sm focus:outline-none focus:ring focus:border-atlantis-300 rounded'>
            <PlusIcon display={'inline'} className='pr-1'/> Import Users
        </button>
        <Dialog
            hasClose={true}
            shouldCloseOnOverlayClick={false}
            isShown={showPopup}
            header={
                <Pane
                    className="w-full flex justify-between">
                    <Heading size={600} fontWeight="bolder">Import Users</Heading>
                    <CloseButton className="mr-2" onClick={closePopup}/>
                </Pane>
            }
            hasHeader={true}
            hasFooter={false}
            width={'70%'}
            containerProps={{
                maxHeight: USER_POPUP_HEIGHT,
                marginY: `calc((100vh - ${USER_POPUP_HEIGHT}px) / 2)`,
                overflowY: 'auto'
            }}
            onCloseComplete={closePopup}
        >
            <CsvImport successCallback={successCallback} closePopupCallback={closePopup}/>
        </Dialog>
    </>)
}

export type ProviderUserImportFormInput = {
    providerGuid: string;
    imports: ProviderUserImportInput[];
}

type CsvImportProps = {
    closePopupCallback: () => void;
    successCallback: () => void;
}
const CsvImport = ({successCallback, closePopupCallback}: CsvImportProps) => {
    const currentUser = useCurrentUser();
    let isBhpnAdmin = ['THE_BHPN_ADMIN', 'THE_BHPN_ADMIN_WITH_PROVIDER_SUPPORT'].includes(currentUser?.role ?? '');
    const applicationRoot = useApplicationRoot();
    if (applicationRoot === ApplicationRootUrl.AZURE_INTERNAL_UM_PROVIDER_ROUTE_NAME) {
        isBhpnAdmin = false;  
    }
    const {isSubmitting$, submit, methods} = useValidateProviderUserImportInput(successCallback)

    return (
        <div className="overflow-auto flex flex-col flex-grow mb-4 scrollbar-always">
            <FormProvider {...methods}>
                <form>

                    {isBhpnAdmin ?
                        <AutocompleteProviderInput/> :
                        <ReadonlyProviderInput providerGuid={currentUser?.provider?.guid ?? ''}
                                               providerName={currentUser?.provider?.name ?? ''}/>}
                    <CSVImportUserInput isSubmitting$={isSubmitting$}/>
                </form>
                <br/>
                <div style={{height: 40}}></div>
                <Pane display='flex' justifyContent='right'
                      position="absolute"
                      bottom={0}
                      right={24}
                      paddingTop={12} paddingLeft={24} width='100%'
                >
                    <CancelButton isSubmitting$={isSubmitting$} cancel={closePopupCallback}/>
                    <OkButton text='Import' isSubmitting$={isSubmitting$} submit={submit}/>
                </Pane>
            </FormProvider>
        </div>
    )
}

export const sanitizeDataForMutation = (formData: ProviderUserImportFormInput) => {
    return {
        providerGuid: formData.providerGuid,
        imports: formData.imports.map((row) => {
                return {
                    email: row.email,
                    firstName: row.firstName,
                    lastName: row.lastName,
                    referralManagementAccess: row.referralManagementAccess,
                    clientCareAccess: row.clientCareAccess,
                    providerMetricsAccess: row.providerMetricsAccess,
                    rosterAdministrationAccess: row.rosterAdministrationAccess,
                    userManagementAccess: row.userManagementAccess,
                    // Named here or it never leaves the browser. This rebuilds each row field by
                    // field, and the GraphQL input fills in its own default for a missing grant, so
                    // a dropped value imports every row the same with nothing anywhere looking wrong.
                    auditLogAccess: row.auditLogAccess,
                    contactEditAccess: row.contactEditAccess,
                    contactReadAccess: row.contactReadAccess,
                }
            }
        )
    };
}

const useValidateProviderUserImportInput = (successCallback: () => void) => {
    const {submit, isRunning$} = useProviderUserImportMutation$(successCallback);
    const methods = useForm<ProviderUserImportFormInput, object>({});
    const {trigger, getValues} = methods;

    const submitProviderUsers = useCallback(async () => {
        const isValid = await trigger();
        if (isValid) {
            await submit(sanitizeDataForMutation(getValues()));
        }
    }, [trigger, getValues, submit])
    return {
        methods,
        isSubmitting$: isRunning$,
        submit: submitProviderUsers,
    }
}
